import type { ConnectorConnectionStatus } from "../atoms/ConnectorStatusBadge";

export interface ConnectorActionDescriptor {
  label: string;
  withChevron: boolean;
}

/**
 * Derive the footer affordance from a registry row.
 *
 * - `not_connected` → "Connect" (no chevron) — primary CTA into the connect flow.
 * - any connected state → "{n} instance(s)" with chevron — drills into the connections list.
 *
 * `instanceCount` takes precedence; otherwise `connectionId` implies one instance.
 */
export const getConnectorAction = (
  status: ConnectorConnectionStatus,
  instanceCount?: number,
  connectionId?: string | null,
): ConnectorActionDescriptor => {
  if (status === "not_connected") {
    return { label: "Connect", withChevron: false };
  }

  if (typeof instanceCount === "number") {
    return {
      label: `${instanceCount} ${instanceCount === 1 ? "instance" : "instances"}`,
      withChevron: true,
    };
  }

  if (connectionId) {
    return { label: "1 instance", withChevron: true };
  }

  return { label: "Connect", withChevron: false };
};

/**
 * Accessible name of the card button. A coming-soon card is not an "Open"
 * action, so its name says the connector and why it is unavailable instead.
 */
export const getConnectorCardAriaLabel = (
  displayName: string,
  comingSoon?: { comingSoonLabel: string },
): string => {
  if (!comingSoon) return `Open ${displayName} connector`;
  return comingSoon.comingSoonLabel
    ? `${displayName} connector, ${comingSoon.comingSoonLabel}`
    : `${displayName} connector`;
};
